/**
 * Web Push subscription API.
 *
 * Lets the frontend register, list, and unregister browser/PWA push
 * subscriptions with the web-backend so the scheduler worker can deliver
 * "notify on complete" notifications via VAPID.
 */

import { get, post, del } from './client';

const BASE_PATH = '/v1/push-subscriptions';

export interface VapidPublicKeyResponse {
  public_key: string;
}

export interface PushSubscriptionRegisterPayload {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
  user_agent?: string | null;
}

export interface MySubscriptionSummary {
  id: string;
  user_agent: string | null;
  created_at: string | null;
  last_used_at: string | null;
}

/**
 * Fetch the server's VAPID public key. The browser uses it as
 * `applicationServerKey` when calling `pushManager.subscribe()`.
 */
export function getVapidPublicKey(): Promise<VapidPublicKeyResponse> {
  return get<VapidPublicKeyResponse>(`${BASE_PATH}/vapid-public-key`);
}

/**
 * Upsert the current browser's push subscription on the server.
 *
 * Idempotent — re-registering the same endpoint refreshes the row.
 */
export function registerPushSubscription(
  payload: PushSubscriptionRegisterPayload
): Promise<{ status: string }> {
  return post<{ status: string }>(BASE_PATH, payload);
}

/**
 * Remove a previously-registered subscription (by endpoint).
 */
export async function unregisterPushSubscription(endpoint: string): Promise<void> {
  await del(BASE_PATH, { data: { endpoint } });
}

/**
 * List the authenticated user's active push subscriptions (metadata only).
 */
export function listMyPushSubscriptions(): Promise<MySubscriptionSummary[]> {
  return get<MySubscriptionSummary[]>(`${BASE_PATH}/me`);
}
